import * as React from "react"
import { makeStyles } from "@material-ui/core"
import theme from "../../theme"
import { Pagination } from "@material-ui/lab"
import { CommonTableTwo } from "../table"
import { currentUserHasMoney, MONEY_MASK } from "../../lib/permissions"

const useStyles = makeStyles((props) => ({
  root: {
    width: "calc(100vw - 350px)",
    borderRadius: "15px",
    backgroundColor: theme.colors.White,
    padding: "12px",
    boxShadow: " 20px 20px 60px #e6e6e6,-20px -20px 60px #ffffff",
    marginRight: "220px",
    marginTop: "15px"
  },
  tableHeader: {
    fontSize: "24px",
    marginBottom: "8px",
  },
  profileImage: {
    width: "48px",
    height: "48px",
    borderRadius: "24px",
    cursor: "pointer",
    objectFit: "cover",
  },
  headerName: {
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
  },
  name: {
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
    fontSize: "15px",
  },
  sub: {
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.Violet,
    opacity: 0.6,
    fontSize: "12px",
    marginTop: "2px",
  },
  badgeRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    marginTop: "5px",
  },
  badge: {
    display: "inline-block",
    padding: "1px 7px",
    borderRadius: "999px",
    fontSize: "10.5px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    lineHeight: "16px",
  },
  strong: {
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
    fontSize: "14px",
  },
}))

const formatMoney = (value, currency = "NGN") => {
  // financials.view gate: mask monetary values for users without it.
  if (!currentUserHasMoney()) return MONEY_MASK
  const n = Number(value)
  if (value === null || value === undefined || Number.isNaN(n)) return "—"
  try {
    return new Intl.NumberFormat("en-NG", { style: "currency", currency }).format(n)
  } catch (e) {
    return n.toLocaleString()
  }
}

const fullName = (row) => {
  const name = [row?.first_name, row?.last_name].filter(Boolean).join(" ").trim()
  return name || row?.email || "—"
}

export default function UserDetailsResultBoard({
  users, totalUsers, onRowClick, searchFilters, setSearchFilters, ...others
}) {
  const classes = useStyles(others)
  const [selectedColId, setSelectedColId] = React.useState(null)

  const RoleBadges = ({ row }) => {
    const badges = []
    if (row?.is_admin) badges.push({ key: "admin", label: "Admin", color: theme.colors.DodgerBlue, bg: theme.colors.Zumthor })
    if (row?.is_hr) badges.push({ key: "hr", label: "HR", color: theme.colors.ApproveGreen, bg: "#DCFCE7" })
    if (row?.is_staff) badges.push({ key: "staff", label: "Staff", color: "#7C3AED", bg: "#EDE9FE" })
    if (!badges.length) badges.push({ key: "employee", label: "Employee", color: theme.colors.Violet, bg: theme.colors.Gallery })
    return (
      <div className={classes.badgeRow}>
        {badges.map((b) => (
          <span key={b.key} className={classes.badge} style={{ color: b.color, backgroundColor: b.bg }}>
            {b.label}
          </span>
        ))}
        {row?.is_active === false ? (
          <span className={classes.badge} style={{ color: theme.colors.RejectRed, backgroundColor: theme.colors.Cinderella }}>
            Inactive
          </span>
        ) : null}
      </div>
    )
  }

  return (
    <section className={classes.root}>

      <header className={classes.tableHeader}>Search Results</header>
      <CommonTableTwo
        data={
          users
        }
        selectedColId={selectedColId}
        onRowClick={
          (each, rowIndex) => {
            if (onRowClick) {
              onRowClick(each, rowIndex)
              setSelectedColId(rowIndex)
            }
          }
        }
        columns={[
          {
            Header: "Photo",
            HeaderCell: (data) => <div className={classes.headerName}>{data}</div>,
            Cell: (data, row) => (
              <img className={classes?.profileImage} src={row?.image || "/images/profile-ico.svg"} alt={row?.first_name} />
            ),
          },
          {
            Header: "Employee",
            HeaderCell: (data) => <div className={classes.headerName}>{data}</div>,
            Cell: (data, row) => (
              <div>
                <div className={classes.name}>{fullName(row)}</div>
                <div className={classes.sub}>{row?.email || "—"}</div>
                <RoleBadges row={row} />
              </div>
            ),
          },
          {
            Header: "Company",
            HeaderCell: (data) => <div className={classes.headerName}>{data}</div>,
            Cell: (data, row) => (
              <div>
                <div className={classes.strong}>{row?.company?.name || "----------"}</div>
                <div className={classes.sub}>
                  {[row?.company?.country, row?.company?.currency].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
            ),
          },
          {
            Header: "Position",
            HeaderCell: (data) => <div className={classes.headerName}>{data}</div>,
            Cell: (data, row) => (
              <div>
                <div className={classes.strong}>{row?.profile?.position || "—"}</div>
                <div className={classes.sub}>{row?.profile?.phone || "—"}</div>
              </div>
            ),
          },
          {
            Header: "Status",
            HeaderCell: (data) => <div className={classes.headerName}>{data}</div>,
            Cell: (data, row) => (
              <div>
                <span
                  className={classes.badge}
                  style={{
                    color: row?.is_active === false ? theme.colors.RejectRed : theme.colors.ApproveGreen,
                    backgroundColor: row?.is_active === false ? theme.colors.Cinderella : "#DCFCE7",
                  }}
                >
                  {row?.is_active === false ? "Inactive" : "Active"}
                </span>
                {row?.state ? <div className={classes.sub}>{row.state}</div> : null}
              </div>
            ),
          },
          {
            Header: "Balance",
            HeaderCell: (data) => <div className={classes.headerName}>{data}</div>,
            Cell: (data, row) => (
              <div>
                <div className={classes.strong}>
                  {formatMoney(row?.current_balance, row?.company?.currency)}
                </div>
                <div className={classes.sub}>
                  Limit: {formatMoney(row?.withdrawal_limit, row?.company?.currency)}
                </div>
              </div>
            ),
          },
        ]}
      />
      <div className={"pagination"}>
        <Pagination
          page={searchFilters.page}
          count={Math.ceil(totalUsers / searchFilters.page_size)}
          showFirstButton
          showLastButton
          onChange={(event, value) => {
            setSearchFilters((state) => ({ ...state, page: value }));
          }}
        />
      </div>
    </section>

  )
}
